import React from 'react';
import { t, Trans } from '@lingui/macro';

import FaqSection from '@rover/components/src/FaqSection';
import { FaqParagraph } from '@rover/components/src/FrequentlyAskedQuestions';
import { Link } from '@rover/kibble/core';
import UnorderedList from '@rover/react-lib/src/components/lists/UnorderedList/UnorderedList';
import { camelize } from '@rover/rsdk/src/modules/Network/object-utilities';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';

type FaqSectionProps = React.ComponentProps<typeof FaqSection>;
type Props = Omit<FaqSectionProps, 'faqItems'> & {
  roverGuaranteeLink: string;
  cxOperatingHours: string;
};

const HomePageFaqs: React.FC<Props> = ({
  roverGuaranteeLink,
  cxOperatingHours: customerSupportLocalized,
  ...sectionProps
}) => {
  const { navigation, currentUrls } = useCountryConfigurationShim();

  if (!navigation || !currentUrls) {
    return null;
  }

  const { localizedLinkUrls } = camelize(navigation.urls, true);

  return (
    <FaqSection
      {...sectionProps}
      faqItems={[
        // 1
        {
          itemHeading: t`What is Rover?`,
          itemBody: (
            <>
              <FaqParagraph>
                <Trans>
                  Rover is a place to find and connect with local pet sitters and service providers.
                  Book trusted care for dogs and cats, whenever and wherever you need it!
                </Trans>
              </FaqParagraph>
              <FaqParagraph>
                {navigation.hasDogTraining ? (
                  <Trans comment="this will be used only for locations that support dog training">
                    With Rover, you can discover loving pet care services in your neighborhood: pet
                    boarding, house sitting, dog walking, drop-in visits, doggy day care, and dog
                    training. We make it easy to get personalized care for your one-of-a-kind pet.
                  </Trans>
                ) : (
                  <Trans comment="this will be used for locations that don't support dog training">
                    With Rover, you can discover loving pet care services in your neighborhood: pet
                    boarding, house sitting, dog walking, drop-in visits, and doggy day care. We
                    make it easy to get personalized care for your one-of-a-kind pet.
                  </Trans>
                )}
              </FaqParagraph>
            </>
          ),
        },
        // 2
        {
          itemHeading: t`How does Rover work?`,
          itemBody: (
            <>
              <FaqParagraph>
                {navigation.hasDogTraining ? (
                  <Trans comment="this will be used only for locations that support dog training">
                    As a pet parent, you can search for local pet care on Rover by entering your
                    address or zip code. Then, explore sitter, walker, and trainer profiles and
                    learn more about their pet care experience, ratings, and reviews.
                  </Trans>
                ) : (
                  <Trans comment="this will be used for locations that don't support dog training">
                    As a pet parent, you can search for local pet care on Rover by entering your
                    address or zip code. Then, explore sitter and walker profiles and learn more
                    about their pet care experience, ratings, and reviews.
                  </Trans>
                )}
              </FaqParagraph>
              <FaqParagraph>
                <Trans>
                  While it’s not required, Rover recommends scheduling Meet & Greets with pet care
                  providers you’re interested in before booking. This is your opportunity to make
                  sure a sitter is the right match for your pet's unique needs. Once you and the
                  sitter agree it’s a good fit, complete the booking and payment through Rover. You
                  can message your sitter anytime before, during, or after the booking!
                </Trans>
              </FaqParagraph>
              <FaqParagraph>
                {navigation.hasDogTraining ? (
                  <Trans comment="this will be used only for locations that support dog training">
                    Many sitters, walkers, and trainers offer multiple services on Rover, making it
                    easy to book consistent and flexible care for your pets. For example: If your
                    dog usually just needs a dog walker, but later on you need boarding over a long
                    weekend, you can often book the same person for both situations. To see what
                    other services a pet care provider offers, check out the “Services” tab on their
                    profile.
                  </Trans>
                ) : (
                  <Trans comment="this will be used for locations that don't support dog training">
                    Many sitters and walkers offer multiple services on Rover, making it easy to
                    book consistent and flexible care for your pets. For example: If your dog
                    usually just needs a dog walker, but later on you need boarding over a long
                    weekend, you can often book the same person for both situations. To see what
                    other services a pet care provider offers, check out the “Services” tab on their
                    profile.
                  </Trans>
                )}
              </FaqParagraph>
              <FaqParagraph>
                {navigation.hasDogTraining ? (
                  <Trans comment="this will be used only for locations that support dog training">
                    Interested in becoming a sitter, walker, or trainer on Rover? Get started{' '}
                    <Link href={currentUrls.becomeASitterUrl}>here</Link>.
                  </Trans>
                ) : (
                  <Trans comment="this will be used for locations that don't support dog training">
                    Interested in becoming a sitter or walker on Rover? Get started{' '}
                    <Link href={currentUrls.becomeASitterUrl}>here</Link>.
                  </Trans>
                )}
              </FaqParagraph>
            </>
          ),
        },
        // 3
        {
          itemHeading: t`How does Rover promote safe experiences?`,
          itemBody: (
            <>
              <FaqParagraph>
                <Trans comment="For locales that don't do background checks, substitue with 'identity verification'. Variable customerSupportLocalized is a pre-translated country-specific phrase, i.e., '24/7 support'">
                  Rover promotes safe experiences in multiple ways, like requiring all pet care
                  providers joining our community to pass a background check. Every booking also
                  includes reservation protection, {customerSupportLocalized}, and the{' '}
                  <Link href={roverGuaranteeLink} asInlineLink>
                    Rover Guarantee
                  </Link>
                  . Everyone on Rover has access to a block and report feature. In the unlikely
                  event you need to report any messages, you can easily do so to{' '}
                  <Link href={currentUrls.extenuatingCircumstancesHelpdeskUrl} asInlineLink>
                    Rover Support
                  </Link>
                  .
                </Trans>
              </FaqParagraph>
              <FaqParagraph>
                <Trans>
                  We encourage all pet parents and sitters to read verified Rover reviews and
                  schedule Meet & Greets before booking. Meet & Greets are in-person meetings where
                  the pet, pet parent, and pet care provider can get to know each other. This allows
                  everyone to assess if the environment, skills, and experience align with each
                  pet's unique needs. Check out our{' '}
                  <Link href={localizedLinkUrls.meetAndGreetGuide} asInlineLink>
                    Meet & Greet Guide
                  </Link>{' '}
                  for more tips.
                </Trans>
              </FaqParagraph>
            </>
          ),
        },
        // 4
        {
          itemHeading: t`How do I find a sitter?`,
          itemBody: (
            <>
              <FaqParagraph>
                <Trans>
                  The best way to find a sitter who’s a great match for your pet is to first create
                  a Rover account and fill out a pet profile. The more we know about your pet, the
                  easier it is to help you find a sitter who’s a great match for them.
                </Trans>
              </FaqParagraph>
              <FaqParagraph>
                <Trans>
                  On the search page, you can{' '}
                  <Link href="/search/" asInlineLink>
                    filter sitters
                  </Link>{' '}
                  based on your pet’s unique care needs, like their size, spay/neuter status,
                  whether they need grooming, if you’d like your sitter to have a fenced-in yard,
                  etc. Some features, like weekly scheduling or full-time care, may vary based on
                  the service type.
                </Trans>
              </FaqParagraph>
              <FaqParagraph>
                <Trans>
                  Add your information and search to see a list of sitters who match your criteria.
                  You can move around the map to refresh your sitter options and find sitters closer
                  to your home, work, or wherever you need care. Open sitter profiles to explore
                  recent reviews, read about their care style, and see how to contact them.
                </Trans>
              </FaqParagraph>
            </>
          ),
        },
        // 5
        {
          itemHeading: t`How much does booking a service on Rover cost?`,
          itemBody: (
            <>
              <FaqParagraph>
                {navigation.hasDogTraining ? (
                  <Trans>
                    Rates vary depending on the service, location, and experience of the pet care
                    provider. This is because pet sitters, boarders, dog walkers, and trainers{' '}
                    <Link
                      href={localizedLinkUrls.howMuchDoesItCostToBookASitterOrDogWalker}
                      asInlineLink
                    >
                      set their own rates on Rover
                    </Link>
                    . You may notice higher rates during holidays or for add-on services like
                    grooming, pickup and drop-off, additional pets, and extended stays.
                  </Trans>
                ) : (
                  <Trans>
                    Rates vary depending on the service, location, and experience of the pet care
                    provider. This is because pet sitters, boarders, and dog walkers{' '}
                    <Link
                      href={localizedLinkUrls.howMuchDoesItCostToBookASitterOrDogWalker}
                      asInlineLink
                    >
                      set their own rates on Rover
                    </Link>
                    . You may notice higher rates during holidays or for add-on services like
                    grooming, pickup and drop-off, additional pets, and extended stays.
                  </Trans>
                )}
              </FaqParagraph>
              <FaqParagraph>
                <Trans>
                  To get an idea of typical rates in your area, try a search on Rover. Enter your
                  location and include as many details as you can. To make sure you’re seeing
                  correct price estimates,{' '}
                  <Link href={localizedLinkUrls.howDoIAddEditOrDeleteAPetProfile} asInlineLink>
                    complete an individual profile for each of your pets
                  </Link>{' '}
                  before you search.
                </Trans>
              </FaqParagraph>
            </>
          ),
        },
        // 6
        {
          itemHeading: t`What does a dog or cat’s day with a sitter look like?`,
          itemBody: (
            <>
              <FaqParagraph>
                <Trans>
                  This depends on the service, your pet’s care instructions, and the sitter, but
                  generally speaking: A typical drop-in or overnight stay includes feeding, fresh
                  water, potty breaks or litter box cleaning, and playtime. We know pets thrive on
                  familiar routines, so Rover makes it possible to find a sitter who’ll maintain
                  your pet’s daily rituals—whether they like company during dinnertime or require a
                  minimum number of ball throws before bed.
                </Trans>
              </FaqParagraph>
              <FaqParagraph>
                <Trans>
                  Looking for sitters who really go the extra mile? Some offer what can only be
                  described as luxury boarding, and they’ll detail how they structure a pet’s day in
                  the “A typical day” section.
                </Trans>
              </FaqParagraph>
            </>
          ),
        },
        // 7
        {
          itemHeading:
            navigation && navigation.hideBackgroundCheckVerbiage
              ? t`What kind of identity verification do pet sitters on Rover go through?`
              : t`What kind of background check do pet sitters on Rover have?`,
          itemBody: (
            <>
              {navigation && navigation.hideBackgroundCheckVerbiage ? (
                <FaqParagraph>
                  <Trans comment="this will only be shown in areas that don't use background checks">
                    In the UK and Europe, sitters joining Rover must{' '}
                    <Link href={localizedLinkUrls.sitterIdVerified} asInlineLink>
                      verify their identity
                    </Link>{' '}
                    before they are listed on Rover. You can confirm a sitter’s identity
                    verification by looking for the green badge on their profile.
                  </Trans>
                </FaqParagraph>
              ) : (
                <>
                  <FaqParagraph>
                    <Trans comment="this will only be shown in areas that support background checks">
                      In North America, you can find the{' '}
                      <Link href="/background-checks" asInlineLink>
                        type of background check
                      </Link>{' '}
                      a sitter has passed by looking at their profile:
                    </Trans>
                  </FaqParagraph>
                  <UnorderedList>
                    <Trans comment="an item in a list of badge types - will only be shown in areas that support background checks">
                      A blue badge means the sitter has passed a basic background check.
                    </Trans>
                    <Trans comment="an item in a list of badge types - will only be shown in areas that support background checks">
                      A gold badge means the sitter has passed an enhanced background check.
                    </Trans>
                  </UnorderedList>
                </>
              )}
              <FaqParagraph>
                <Trans>
                  For extra peace of mind, you can read verified reviews from other pet parents and
                  check each sitter's repeat client stats. We also encourage you to schedule a Meet
                  & Greet before booking with a new sitter or walker to meet them, ask questions,
                  and see how they interact with your pets. For more details, visit{' '}
                  <Link href={localizedLinkUrls.safetyWithLocale} asInlineLink>
                    Rover's Trust & Safety page
                  </Link>
                  .
                </Trans>
              </FaqParagraph>
            </>
          ),
        },
        // 8
        {
          itemHeading: t`What kind of pets can sitters on Rover watch?`,
          itemBody: (
            <FaqParagraph>
              <Trans>
                Rover was created to make it easy to find loving care for dogs and cats of any age,
                from puppies and kittens to senior pets. Some pet sitters do understand how to care
                for other pet types, such as birds, bunnies, lizards, or fish, and they may list
                this experience in their profiles, however Rover is intended for dog and cat care.
              </Trans>
            </FaqParagraph>
          ),
        },
        // 9
        {
          itemHeading: t`Can I find available pet sitters on short notice through Rover?`,
          itemBody: (
            <>
              <FaqParagraph>
                <Trans>
                  If you need a pet sitter on short notice, start by entering your dates into a
                  Rover search. Look for the green checkmark at the bottom of each listing to find
                  sitters who have recently updated their availability. Star Sitter status is also a
                  good indicator that a sitter is likely to respond quickly to your message. The{' '}
                  <Link href="https://www.rover.com/star-sitter/" asInlineLink>
                    Star Sitter program
                  </Link>{' '}
                  recognizes pet sitters and dog walkers with a track record of highly-rated care
                  for pets and responsive, reliable service for pet parents on Rover.
                </Trans>
              </FaqParagraph>
              <FaqParagraph>
                <Trans>
                  Not seeing the sitters you need? Try broadening your search to include sitters who
                  live a little farther away.
                </Trans>
              </FaqParagraph>
              <FaqParagraph>
                <Trans>
                  <strong>Tip:</strong> In a time crunch? When searching, reach out to a few sitters
                  at a time to find the best fit for your family even faster.
                </Trans>
              </FaqParagraph>
            </>
          ),
        },
        // 10
        {
          itemHeading: t`How do I know a pet sitter is reliable?`,
          itemBody: (
            <>
              <FaqParagraph>
                <Trans>
                  Rather than relying on one factor, Rover offers multiple trust signals to help pet
                  parents evaluate a pet sitter’s reliability. Background checks, star ratings,
                  repeat clients, photo updates, and Star Sitter status are all ways to identify a
                  reliable sitter.
                </Trans>
              </FaqParagraph>
              <FaqParagraph>
                <Trans>
                  Another important step is a Meet & Greet or trial day. Both methods offer a way
                  for the pet parent to verify whether a pet sitter is the right fit. Trial days are
                  booked as regular services, like doggy day care or overnight boarding, and can
                  serve as a low-pressure practice run to see how your pet interacts with their
                  potential sitter before committing to a longer stay. Successful Meet & Greets and
                  trial days can help reduce your pet’s stress during the actual stay and give you
                  peace of mind while you’re busy or traveling.
                </Trans>
              </FaqParagraph>
            </>
          ),
        },
      ]}
    >
      <FaqSection.Header webProps={{ responsive: false }}>
        <Trans>FAQs</Trans>
      </FaqSection.Header>
      <FaqSection.Subheader>
        <Trans>Frequently asked questions about Rover</Trans>
      </FaqSection.Subheader>
    </FaqSection>
  );
};

export default HomePageFaqs;
